import LayeredAutocompleteSelect from "../components/LayeredAutocompleteSelect";
import React, { useState, useEffect, useMemo } from "react";
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  TableContainer,
  Paper,
  IconButton,
  Tooltip,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { NeoButton } from "../shared/components";
import CustomWorkflowApi, { type CustomWorkflow } from "../apis/customWorkflowApi";
import { useIntegration } from "../hooks/useIntegration";
import { Customer } from "types/integration";
import { isMspOrgCustomer } from "../utils/mspOrgCustomer";
import { usePublishConfigurationSupport } from "../configurationSupport/ConfigurationSupportContext";
import GlobalLoadingOverlay from "../components/GlobalLoadingOverlay";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { format } from "date-fns";
import { useUserMsp } from "../hooks/useUserMsp";

const customWorkflowApi = new CustomWorkflowApi();

const CUSTOMER_QUERY_KEY = "customer";
/** URL / Select value when "all customers" and there is no PSA `root` row in the list. */
const ALL_CUSTOMERS_LIST_VALUE = "all";

const CustomWorkflows: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    customers,
    isLoading: customersLoading,
    getCustomers,
  } = useIntegration();
  const { userMspInfo, isLoading: userMspLoading } = useUserMsp();

  const [workflows, setWorkflows] = useState<CustomWorkflow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [workflowToCopy, setWorkflowToCopy] = useState<CustomWorkflow | null>(null);
  const [copyTargetCustomerId, setCopyTargetCustomerId] = useState("");

  // Filter customers by MSP ID and include 'All' option
  const filteredCustomers = useMemo(() => {
    const allCustomers = (customers?.rows || customers || []) as Customer[];
    const mspCustomers = allCustomers.filter(
      (c: Customer) =>
        c.name?.toLowerCase() === "root" ||
        String((c as any).msp_id) === String(userMspInfo?.msp_id)
    );
    const rootCustomers = mspCustomers.filter((c: Customer) => c.name?.toLowerCase() === "root");
    const otherCustomers = mspCustomers.filter(
      (c: Customer) => c.name?.toLowerCase() !== "root" && !isMspOrgCustomer(c)
    );
    return [...rootCustomers, ...otherCustomers];
  }, [customers, userMspInfo?.msp_id]);

  const customerMap = useMemo(() => {
    const map: Record<string, string> = {};
    filteredCustomers.forEach((c: Customer) => {
      if (c && c.id) {
        map[c.id] = c.name?.toLowerCase() === "root" ? "All Customer" : c.name;
      }
    });
    return map;
  }, [filteredCustomers]);

  const rootCustomerId = useMemo(() => {
    const root = filteredCustomers.find((c: Customer) => c.name?.toLowerCase() === "root");
    return root?.id?.toString() || null;
  }, [filteredCustomers]);

  /** Resolved selection: URL `?customer=<id>` or omit → All Customer (root id or synthetic `all`). */
  const selectedCustomerId = useMemo(() => {
    if (filteredCustomers.length === 0) return null;
    const allSentinel = rootCustomerId ?? ALL_CUSTOMERS_LIST_VALUE;
    const raw = searchParams.get(CUSTOMER_QUERY_KEY);
    if (!raw || raw.toLowerCase() === "all") return allSentinel;
    const match = filteredCustomers.some((c: Customer) => String(c.id) === raw);
    return match ? raw : allSentinel;
  }, [searchParams, filteredCustomers, rootCustomerId]);

  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  /** Drop invalid `customer` query values so the URL stays shareable and consistent. */
  useEffect(() => {
    if (filteredCustomers.length === 0) return;
    const raw = searchParams.get(CUSTOMER_QUERY_KEY);
    if (!raw || raw.toLowerCase() === "all") return;
    const match = filteredCustomers.some((c: Customer) => String(c.id) === raw);
    if (!match) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(CUSTOMER_QUERY_KEY);
          return next;
        },
        { replace: true }
      );
    }
  }, [filteredCustomers, searchParams, setSearchParams]);

  useEffect(() => {
    if (!selectedCustomerId) return;
    setIsLoading(true);
    const isAllSelected =
      selectedCustomerId === rootCustomerId ||
      selectedCustomerId === ALL_CUSTOMERS_LIST_VALUE ||
      selectedCustomerId === "all";
    const customerIdToFetch = isAllSelected ? undefined : selectedCustomerId;
    customWorkflowApi
      .getWorkflows(customerIdToFetch)
      .then((data: CustomWorkflow[]) => setWorkflows(Array.isArray(data) ? data : []))
      .catch(() => setWorkflows([]))
      .finally(() => setIsLoading(false));
  }, [selectedCustomerId, rootCustomerId]);

  const customerOptions = useMemo(() => {
    const rows = filteredCustomers.map((customer: Customer) => ({
      label: customer.name?.toLowerCase() === "root" ? "All Customer" : customer.name,
      value: customer.id.toString(),
    }));
    const hasAllRow = rows.some((r) => r.label === "All Customer");
    if (hasAllRow) {
      return rows;
    }
    return [{ label: "All Customer", value: ALL_CUSTOMERS_LIST_VALUE }, ...rows];
  }, [filteredCustomers]);

  const copyTargetCustomers = useMemo(
    () =>
      filteredCustomers.filter(
        (customer: Customer) => customer.name?.toLowerCase() !== "root"
      ),
    [filteredCustomers]
  );

  const handleCustomerFilterChange = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const isAllChoice =
          value === ALL_CUSTOMERS_LIST_VALUE || (rootCustomerId != null && value === rootCustomerId);
        if (isAllChoice) {
          next.delete(CUSTOMER_QUERY_KEY);
        } else {
          next.set(CUSTOMER_QUERY_KEY, value);
        }
        return next;
      },
      { replace: false }
    );
  };

  const handleAddWorkflow = () => {
    const isAllSelected =
      selectedCustomerId === rootCustomerId ||
      selectedCustomerId === ALL_CUSTOMERS_LIST_VALUE ||
      selectedCustomerId === "all";
    navigate("/custom_workflows/new", {
      state: {
        selectedCustomer: isAllSelected ? "all" : selectedCustomerId,
        customWorkflowsListSearch: location.search || undefined,
      },
    });
  };

  const handleEditWorkflow = (id: number) => {
    navigate(`/custom_workflows/${id}/edit`, {
      state: { customWorkflowsListSearch: location.search || undefined },
    });
  };

  const handleOpenCopyWorkflow = (workflow: CustomWorkflow) => {
    const firstOtherCustomer = copyTargetCustomers.find(
      (customer: Customer) => String(customer.id) !== String(workflow.customer_id)
    );
    setWorkflowToCopy(workflow);
    setCopyTargetCustomerId(firstOtherCustomer?.id?.toString() || "");
  };

  const handleCloseCopyWorkflow = () => {
    setWorkflowToCopy(null);
    setCopyTargetCustomerId("");
  };

  const handleContinueCopyWorkflow = () => {
    if (!workflowToCopy || !copyTargetCustomerId) return;
    navigate("/custom_workflows/new", {
      state: {
        cloneFromId: workflowToCopy.id,
        selectedCustomer: copyTargetCustomerId,
        customWorkflowsListSearch: location.search || undefined,
      },
    });
    handleCloseCopyWorkflow();
  };

  const handleDeleteWorkflow = (id: number) => {
    // TODO: Open delete confirmation modal
  };

  const workflowsArray = Array.isArray(workflows) ? workflows : [];
  const supportSnapshot = useMemo(
    () => ({
      surface: "custom_workflow" as const,
      title: "Custom workflows",
      configuration: {
        selected_customer_id: selectedCustomerId,
        selected_customer_name: selectedCustomerId ? customerMap[selectedCustomerId] || "" : "",
        loading: isLoading,
        workflows: workflowsArray.slice(0, 40).map((workflow) => ({
          id: workflow.id,
          name: workflow.workflow_name,
          type: workflow.workflow_type || "",
          customer_id: workflow.customer_id,
          step_count: Array.isArray(workflow.steps_json?.steps) ? workflow.steps_json.steps.length : 0,
        })),
        copying: workflowToCopy
          ? {
              id: workflowToCopy.id,
              name: workflowToCopy.workflow_name,
              target_customer_id: copyTargetCustomerId,
            }
          : null,
      },
    }),
    [selectedCustomerId, customerMap, isLoading, workflowsArray, workflowToCopy, copyTargetCustomerId]
  );
  usePublishConfigurationSupport("custom-workflows-list", supportSnapshot, 20);

  return (
    <>
      {(isLoading || customersLoading || userMspLoading) && <GlobalLoadingOverlay />}
      <Box sx={{ px: 1, pt: 2, width: "100%", fontFamily: "var(--theo-font-sans)" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
            gap: 2,
            justifyContent: "flex-end",
          }}
        >
          <LayeredAutocompleteSelect
            fullWidth={false}
            value={selectedCustomerId || ""}
            label="Customer"
            onChange={value => handleCustomerFilterChange(String(value))}
            sx={{
                minWidth: 180,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E0E0E0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#705D4A",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#705D4A",
                },
              }}
            size="small"
            disableClearable
            options={customerOptions}
          />
          <NeoButton variant="neo" onClick={handleAddWorkflow}>
            Add Custom Workflow
          </NeoButton>
        </Box>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            border: "1px solid #E0E0E0",
            maxHeight: "calc(100vh - 180px)",
            overflowY: "auto",
          }}
        >
          <Table stickyHeader sx={{ minWidth: 700, fontFamily: "var(--theo-font-sans)" }}>
            <TableHead>
              <TableRow sx={{ background: "#F7F7F7" }}>
                <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontSize: "12px", fontWeight: 500, color: "#838383" }}>
                  Workflow Name
                </TableCell>
                <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontSize: "12px", fontWeight: 500, color: "#838383" }}>
                  Workflow Type
                </TableCell>
                <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontSize: "12px", fontWeight: 500, color: "#838383" }}>
                  Customer
                </TableCell>
                <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontSize: "12px", fontWeight: 500, color: "#838383" }}>
                  Steps
                </TableCell>
                <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontSize: "12px", fontWeight: 500, color: "#838383" }}>
                  Created By
                </TableCell>
                <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontSize: "12px", fontWeight: 500, color: "#838383" }}>
                  Last Updated By
                </TableCell>
                <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontSize: "12px", fontWeight: 500, color: "#838383" }}>
                  Created At
                </TableCell>
                <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontSize: "12px", fontWeight: 500, color: "#838383", width: 140 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workflowsArray.map((wf: CustomWorkflow, idx: number) => (
                <TableRow
                  key={wf.id}
                  sx={{
                    background: idx % 2 === 0 ? "#FAFAFA" : "#FFF",
                    "&:hover": { background: "#F0F0F0" },
                    transition: "background 0.2s",
                  }}
                >
                  <TableCell sx={{ py: 1.5, fontSize: 14, fontFamily: "var(--theo-font-sans)" }}>
                    <Link
                      component={RouterLink}
                      to={`/custom_workflows/${wf.id}/edit`}
                      state={{ customWorkflowsListSearch: location.search || undefined }}
                      underline="hover"
                      sx={{
                        color: "#705D4A",
                        fontWeight: 500,
                        textDecoration: "none",
                        "&:hover": { color: "#5a4a3a" },
                      }}
                    >
                      {wf.workflow_name}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ py: 1.5, fontSize: 14, fontFamily: "var(--theo-font-sans)" }}>
                    {wf.workflow_type ?? "—"}
                  </TableCell>
                  <TableCell sx={{ py: 1.5, fontSize: 14, fontFamily: "var(--theo-font-sans)" }}>
                    {wf.customer_id != null ? customerMap[String(wf.customer_id)] || "" : "All"}
                  </TableCell>
                  <TableCell sx={{ py: 1.5, fontSize: 14, fontFamily: "var(--theo-font-sans)" }}>
                    {(() => {
                      const sj = wf.steps_json;
                      if (!sj) return 0;
                      try {
                        const steps = typeof sj === "string" ? JSON.parse(sj || "{}")?.steps : (sj as { steps?: unknown[] })?.steps;
                        return Array.isArray(steps) ? steps.length : 0;
                      } catch {
                        return 0;
                      }
                    })()}
                  </TableCell>
                  <TableCell sx={{ py: 1.5, fontSize: 14, fontFamily: "var(--theo-font-sans)" }}>
                    {wf.created_by ?? "—"}
                  </TableCell>
                  <TableCell sx={{ py: 1.5, fontSize: 14, fontFamily: "var(--theo-font-sans)" }}>
                    {wf.last_updated_by ?? "—"}
                  </TableCell>
                  <TableCell sx={{ py: 1.5, fontSize: 14, fontFamily: "var(--theo-font-sans)" }}>
                    {wf.created_at ? format(new Date(wf.created_at), "PPP") : ""}
                  </TableCell>
                  <TableCell sx={{ py: 1.5, fontFamily: "var(--theo-font-sans)" }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEditWorkflow(wf.id)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        copyTargetCustomers.some(
                          (customer: Customer) => String(customer.id) !== String(wf.customer_id)
                        )
                          ? "Copy to another customer"
                          : "No other customers available"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenCopyWorkflow(wf)}
                          disabled={
                            !copyTargetCustomers.some(
                              (customer: Customer) => String(customer.id) !== String(wf.customer_id)
                            )
                          }
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDeleteWorkflow(wf.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {workflowsArray.length === 0 && !isLoading && !userMspLoading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 220,
              opacity: 0.85,
              py: 4,
              fontFamily: "var(--theo-font-sans)",
            }}
          >
            <AccountTreeIcon sx={{ fontSize: 48, mb: 1, color: "#9B7AEF" }} />
            <Typography variant="h6" sx={{ fontWeight: 500, color: "#705D4A", fontFamily: "var(--theo-font-sans)" }}>
              No custom workflows found
            </Typography>
            <Typography variant="body2" sx={{ color: "#6C6C6C", mt: 1, fontFamily: "var(--theo-font-sans)" }}>
              Create multi-step workflows for user onboarding and other repeatable processes. Click{" "}
              <b>Add Custom Workflow</b> to get started.
            </Typography>
          </Box>
        )}
      </Box>
      <Dialog open={Boolean(workflowToCopy)} onClose={handleCloseCopyWorkflow} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600 }}>
          Copy workflow to another customer
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <Typography sx={{ mb: 2, color: "#666", fontFamily: "var(--theo-font-sans)" }}>
            Start with a copy of <strong>{workflowToCopy?.workflow_name}</strong>, then review and customize it before creating it.
          </Typography>
          <LayeredAutocompleteSelect
            value={copyTargetCustomerId}
            label="Target customer"
            onChange={value => setCopyTargetCustomerId(String(value))}
            size="small"
            fullWidth
            disableClearable
            options={copyTargetCustomers.filter(customer => workflowToCopy == null || String(customer.id) !== String(workflowToCopy.customer_id)).map(customer => ({ value: String(customer.id), label: customer.name }))}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseCopyWorkflow} sx={{ fontFamily: "var(--theo-font-sans)", color: "#666" }}>
            Cancel
          </Button>
          <NeoButton
            variant="neo"
            onClick={handleContinueCopyWorkflow}
            disabled={!copyTargetCustomerId}
          >
            Continue
          </NeoButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomWorkflows;
